<template>
  <div
    id="participant_tile"
    class="relative overflow-hidden flex flex-col"
    :class="isSpeaking ? 'ring-2 ring-blue-500 rounded-lg' : ''"
  >
    <div
      class="relative flex items-center justify-center rounded-lg overflow-hidden participant_tile_area"
    >
      <video
        v-if="videoEnabled"
        ref="videoRef"
        autoplay
        playsinline
        :muted="isLocal"
        class="w-full h-full object-cover rounded-lg"
        :style="isLocal ? { transform: 'scaleX(-1)' } : {}"
      />

      <div v-else class="flex flex-col items-center gap-3">
        <ProfileIcon
          :data="participantUser"
          :size="avatarSize"
          :font="avatarFont"
          :border-radius="100"
        />
        <div class="flex items-center gap-1.5">
          <ThemeImage
            v-if="!audioEnabled"
            class="size-3"
            :icon="imageNames.MUTED_ICON"
            alt="mute"
          />
          <span class="text-xs font-medium truncate text_color">
            {{ displayName }}{{ isLocal ? ' (You)' : '' }}
          </span>
        </div>
      </div>

      <div
        v-if="videoEnabled"
        class="absolute bottom-2 left-2 flex items-center gap-1.5 rounded px-2 py-0.5 participant_name_overlay"
      >
        <ThemeImage
            v-if="!audioEnabled"
            class="size-3"
            :icon="imageNames.MUTED_ICON"
            alt="mute"
        />
        <span class="text-xs font-medium truncate text_color">
          {{ displayName }}{{ isLocal ? ' (You)' : '' }}
        </span>
      </div>
    </div>

    <audio v-if="!isLocal" ref="audioRef" autoplay />
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { Track } from "livekit-client";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { renderEntity } from "@/src/utils/utils.js";
import { livekitManager } from "@/src/services/chats/livekitManager.js";

const props = defineProps({
  userId: { type: [Number, String], required: true },
  isLocal: { type: Boolean, default: false },
  videoEnabled: { type: Boolean, default: false },
  audioEnabled: { type: Boolean, default: true },
  isSpeaking: { type: Boolean, default: false },
  avatarSize: { type: Number, default: 120 },
  avatarFont: { type: Number, default: 40 },
});

const store = useStore();
const videoRef = ref(null);
const audioRef = ref(null);
let attachedVideoTrack = null;
let attachedAudioTrack = null;

const allUsers = computed(() => store.getters["users/users"] || []);
const currentUserId = computed(() => store.getters["auth/userData"]?.id);

const participantUser = computed(() => {
  const targetId = props.isLocal ? currentUserId.value : props.userId;
  const user = allUsers.value.find((u) => Number(u.id) === Number(targetId));
  return user || { id: targetId, name: `User ${targetId}` };
});

const displayName = computed(() => renderEntity(participantUser.value));

const attachLocalVideo = () => {
  try {
    if (!videoRef.value) return;
    const track = livekitManager.getLocalVideoTrack();
    if (track && track.mediaStreamTrack) {
      const stream = new MediaStream([track.mediaStreamTrack]);
      videoRef.value.srcObject = stream;
      attachedVideoTrack = track;
    }
  } catch (error) {
    console.error("ParticipantTile: failed to attach local video", error);
  }
};

const attachRemoteVideo = () => {
  try {
    const room = livekitManager.getRoom();
    if (!room || !videoRef.value) return;

    const participant = livekitManager.findRemoteParticipant(props.userId);
    if (!participant) return;

    const pub = participant.getTrackPublication(Track.Source.Camera);
    const track = pub?.track;
    if (track) {
      if (attachedVideoTrack) {
        attachedVideoTrack.detach(videoRef.value);
      }
      track.attach(videoRef.value);
      attachedVideoTrack = track;
    }
  } catch (error) {
    console.error("ParticipantTile: failed to attach remote video", error);
  }
};

const attachRemoteAudio = () => {
  try {
    const room = livekitManager.getRoom();
    if (!room || !audioRef.value || props.isLocal) return;

    const participant = livekitManager.findRemoteParticipant(props.userId);
    if (!participant) return;

    const pub = participant.getTrackPublication(Track.Source.Microphone);
    const track = pub?.track;
    if (track) {
      if (attachedAudioTrack) {
        attachedAudioTrack.detach(audioRef.value);
      }
      track.attach(audioRef.value);
      attachedAudioTrack = track;
    }
  } catch (error) {
    console.error("ParticipantTile: failed to attach remote audio", error);
  }
};

const attachTracks = async () => {
  try {
    await nextTick();
    if (props.isLocal) {
      if (props.videoEnabled) attachLocalVideo();
    } else {
      if (props.videoEnabled) attachRemoteVideo();
      attachRemoteAudio();
    }
  } catch (error) {
    console.error("ParticipantTile: failed to attach tracks", error);
  }
};

// Attach when video toggles
watch(() => props.videoEnabled, async (enabled) => {
  try {
    if (!enabled) {
      // Clean up track references immediately before v-if removes the element
      const el = videoRef.value;
      if (el) {
        if (attachedVideoTrack && !props.isLocal) {
          attachedVideoTrack.detach(el);
        }
        el.srcObject = null;
      }
      attachedVideoTrack = null;
    } else {
      await nextTick();
      attachTracks();
    }
  } catch (error) {
    console.error("ParticipantTile: failed to handle video toggle", error);
  }
});

// Attach when audio changes (remote only)
watch(() => props.audioEnabled, () => {
  if (!props.isLocal) attachRemoteAudio();
});

// Re-attach when video element mounts (v-if)
watch(videoRef, (el) => {
  if (el) attachTracks();
});

// Re-attach when audio element mounts
watch(audioRef, (el) => {
  if (el && !props.isLocal) attachRemoteAudio();
});

// Re-attach when participant roster changes (join/leave), not on mute/speaking updates
const participantIds = computed(() => {
  const participants = store.getters["huddles/participants"];
  return participants.map(p => p.user_id).sort().join(",");
});
watch(participantIds, () => {
  if (!props.isLocal) attachTracks();
});

onMounted(() => attachTracks());

onUnmounted(() => {
  try {
    if (attachedVideoTrack && videoRef.value && !props.isLocal) {
      attachedVideoTrack.detach(videoRef.value);
    }
    if (videoRef.value) videoRef.value.srcObject = null;
    if (attachedAudioTrack && audioRef.value) {
      attachedAudioTrack.detach(audioRef.value);
    }
    if (audioRef.value) audioRef.value.srcObject = null;
    attachedVideoTrack = null;
    attachedAudioTrack = null;
  } catch (error) {
    console.error("ParticipantTile: failed to detach tracks on unmount", error);
  }
});

defineExpose({ videoRef, audioRef });
</script>

<style scoped>
.participant_tile_area {
  aspect-ratio: 16/9;
  background: var(--sidebar-item-selected);
}
.participant_name_overlay {
  background: var(--background-color);
  opacity: 0.85;
}
</style>
